<template>
  <div ref="paginationRef" class="sb-pagination" :class="computedClasses">
    <template v-if="carousel || compact">
      <SbPaginationButton
        data-testid="pagination-previous-button"
        icon="chevron-left"
        :button-label="hasLabeledButtons ? i18n(locale, 'back') : undefined"
        :tooltip-label="!hasLabeledButtons ? i18n(locale, 'previousPage') : undefined"
        :disabled="isFirstDisabled"
        @click="handlePreviousPage"
      />
      <SbPaginationPages
        v-if="carousel"
        :pages="pages"
        :current-page="modelValue"
        :locale="locale"
        :parent-width="paginationWidth"
        @change-page="onPageChange"
      />
      <SbPaginationPagesText
        v-else-if="compact"
        :pages="pages"
        :current-page="modelValue"
        show-current-page
        :locale="locale"
      />
      <SbPaginationButton
        data-testid="pagination-next-button"
        icon="chevron-right"
        :tooltip-label="!hasLabeledButtons ? i18n(locale, 'nextPage') : undefined"
        :button-label="hasLabeledButtons ? i18n(locale, 'next') : undefined"
        :disabled="isLastDisabled"
        @click="handleNextPage"
      />
    </template>
    <template v-else>
      <SbPerPageContainer
        :per-page="perPage"
        :per-page-available="perPageAvailable"
        :locale="locale"
        @per-page-change="onPerPageChange"
      />
      <SbPaginationItemsText
        :total="total"
        :current-page="modelValue"
        :pages="pages"
        :per-page="perPage"
        is-placeholder
        :locale="locale"
      />
      <SbPagesContainer
        :pages="pages"
        :current-page="modelValue"
        :locale="locale"
        @page-change="onPageChange"
      />
      <div class="sb-pagination__buttons">
        <div class="sb-pagination__button">
          <SbPaginationButton
            data-testid="pagination-previous-button"
            icon="chevron-left"
            :tooltip-label="i18n(locale, 'previousPage')"
            :disabled="isFirstDisabled"
            @click="handlePreviousPage"
          />
        </div>
        <div class="sb-pagination__button">
          <SbPaginationButton
            data-testid="pagination-next-button"
            icon="chevron-right"
            :tooltip-label="i18n(locale, 'nextPage')"
            :disabled="isLastDisabled"
            @click="handleNextPage"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import i18n, { type Locale } from '../../i18n'
import {
  SbPaginationButton,
  SbPagesContainer,
  SbPaginationPagesText,
  SbPerPageContainer,
  SbPaginationPages,
  SbPaginationItemsText,
} from './components'

const DEFAULT_ROWS_PER_PAGE_DROPDOWN = [25, 50, 75, 100]

export type SbPaginationProps = {
  carousel?: boolean
  compact?: boolean
  customPerPageOptions?: number[]
  perPage?: number
  total?: number
  modelValue: number
  locale?: Locale
  hasLabeledButtons?: boolean
}

const props = withDefaults(
  defineProps<SbPaginationProps>(),
  {
    carousel: false,
    compact: false,
    customPerPageOptions: () => [],
    perPage: 10,
    total: 100,
    locale: 'en',
    hasLabeledButtons: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
  (e: 'next-page', value: number): void
  (e: 'previous-page', value: number): void
  (e: 'page-change', value: number): void
  (e: 'per-page-change', value: number): void
}>()

const pages = computed(() => Math.ceil(props.total / props.perPage))
const isFirstDisabled = computed(() => props.modelValue <= 1)
const isLastDisabled = computed(() => props.modelValue >= pages.value)
const perPageAvailable = computed(() => {
  return props.customPerPageOptions?.length > 0
    ? props.customPerPageOptions
    : DEFAULT_ROWS_PER_PAGE_DROPDOWN
})
const computedClasses = computed(() => ({
  'sb-pagination--carousel': props.carousel,
  'sb-pagination--compact': props.compact,
  'sb-pagination--with-label': props.hasLabeledButtons,
}))

const paginationRef = ref<HTMLElement | null>(null)
const paginationWidth = ref(0)

function updatePaginationWidth() {
  if (paginationRef.value) {
    paginationWidth.value = paginationRef.value.offsetWidth
  }
}
onMounted(() => {
  updatePaginationWidth()
  window.addEventListener('resize', updatePaginationWidth)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', updatePaginationWidth)
})

function handlePreviousPage() {
  if (!isFirstDisabled.value) {
    updateValue(props.modelValue - 1)
    emit('previous-page', props.modelValue - 1)
  }
}

function handleNextPage() {
  if (!isLastDisabled.value) {
    updateValue(props.modelValue + 1)
    emit('next-page', props.modelValue + 1)
  }
}

function onPageChange(page: number) {
  emit('page-change', page)
  updateValue(page)
}

function onPerPageChange(perPage: number) {
  emit('per-page-change', perPage)
  updateValue(1)
}

function updateValue(value: number) {
  emit('update:modelValue', value)
}
</script>
